import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { getStoredUser, clearAuth } from "../lib/api";
import {
  LayoutDashboard, FileText, Building2, Users, Briefcase,
  Upload, FileSpreadsheet, Settings, TrendingUp, Scale, Handshake, Activity, LogOut, KeyRound, Library,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Revenue Intelligence", icon: Activity, testid: "nav-revenue" },
  { to: "/operations", label: "Site Operations", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/negotiation", label: "Negotiation Worksheet", icon: Handshake, testid: "nav-negotiation" },
  { to: "/cta", label: "CTA Library", icon: FileText, testid: "nav-cta" },
  { to: "/rate-card", label: "Rate Card", icon: Library, testid: "nav-rate-card" },
  { to: "/benchmark", label: "Benchmark", icon: Scale, testid: "nav-benchmark" },
  { to: "/sites", label: "Sites & P&L", icon: Building2, testid: "nav-sites" },
  { to: "/studies", label: "Study Profitability", icon: Briefcase, testid: "nav-studies" },
  { to: "/labor", label: "Labor & Timesheets", icon: Users, testid: "nav-labor" },
  { to: "/uploads", label: "Data Uploads", icon: Upload, testid: "nav-uploads" },
  { to: "/reports", label: "Quarterly Reports", icon: FileSpreadsheet, testid: "nav-reports" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Sidebar() {
  const loc = useLocation();
  const nav = useNavigate();
  const user = getStoredUser();
  const signOut = () => {
    clearAuth();
    nav("/login");
  };

  const renderLink = (n) => {
    const Icon = n.icon;
    const active = loc.pathname === n.to || (n.to !== "/" && loc.pathname.startsWith(n.to));
    return (
      <NavLink
        key={n.to}
        to={n.to}
        data-testid={n.testid}
        className={`nav-link flex items-center gap-3 px-4 py-2.5 rounded-md text-[13.5px] mb-0.5 ${
          active ? "nav-link-active bg-white text-slate-900 font-semibold shadow-sm" : "text-slate-600 hover:bg-white/70"
        }`}
      >
        <Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2.25 : 1.75} />
        {n.label}
      </NavLink>
    );
  };

  return (
    <aside className="w-[260px] shrink-0 bg-slate-50 border-r border-border h-screen sticky top-0 flex flex-col" data-testid="app-sidebar">
      <div className="px-6 pt-7 pb-6 flex items-center gap-2.5">
        <div className="relative">
          <TrendingUp className="w-7 h-7" style={{ color: "#0A4C95" }} strokeWidth={2.5} />
          <div className="absolute -right-1 -bottom-1 w-2.5 h-2.5 rounded-full" style={{ background: "#F05D23" }} />
        </div>
        <div>
          <div className="font-bold text-[17px] tracking-tight text-slate-900">Stryde</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 -mt-0.5">Finance</div>
        </div>
      </div>
      <nav className="px-3 flex-1 overflow-auto">
        {NAV.map(renderLink)}
      </nav>
      <div className="p-4 border-t border-border">
        <div className="text-[12px] font-semibold text-slate-800" data-testid="sidebar-user-name">{user?.name || "—"}</div>
        <div className="text-[11px] text-slate-500 mb-2">{user?.email}</div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => nav("/change-password")}
            className="text-[11px] text-slate-500 hover:text-[#0A4C95] inline-flex items-center gap-1"
            data-testid="change-password-link"
          >
            <KeyRound className="w-3 h-3" /> Change password
          </button>
          <span className="text-slate-300">·</span>
          <button
            onClick={signOut}
            className="text-[11px] text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
            data-testid="sign-out-btn"
          >
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}
